import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { FaArrowLeft, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import apiClient from '../services/api'
import { authService } from '../services'
import { useTranslation } from 'react-i18next'

export default function VerifyEmail() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [autoLoginMessage, setAutoLoginMessage] = useState('')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token')
      
      if (!token) {
        setError(t('verifyEmail.error.invalidToken'))
        setLoading(false)
        return
      }
      
      try {
        const response = await apiClient.post('/auth/verify-email', { token })
        
        if (response.data.success) {
          setSuccess(true)
          
          // Try to get email and automatically log in
          if (response.data.email) {
            setAutoLoginMessage(t('verifyEmail.success.autoLoginMessage'))
            try {
              // Get session data from the verify response
              if (response.data.token && response.data.user) {
                // Store the token and session
                localStorage.setItem('authToken', response.data.token)
                
                // Wait briefly to show success message before redirect
                setTimeout(() => {
                  navigate('/flowers')
                }, 1500)
              } else {
                // If auto-login data isn't available, just show success
                setAutoLoginMessage(t('verifyEmail.success.manualLoginMessage'))
              }
            } catch (loginErr) {
              console.error('Auto-login error:', loginErr)
              setAutoLoginMessage(t('verifyEmail.success.manualLoginMessage'))
            }
          }
        } else {
          setError(response.data.message || t('verifyEmail.error.generalError'))
        }
      } catch (err: any) {
        console.error('Email verification error:', err)
        
        // Extract error message from the response if available
        let errorMessage = t('verifyEmail.error.expiredLink')
        
        if (err.response) {
          const responseError = err.response.data?.error
          
          if (responseError) {
            errorMessage = responseError
          }
        } else if (err.message) {
          errorMessage = err.message
        }
        
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    
    verifyEmail()
  }, [searchParams, navigate, t])

  const handleBack = () => {
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        {/* Navbar with circular back button */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <h2 className="text-2xl font-semibold mb-2">{t('verifyEmail.pageTitle')}</h2>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-yellow-400 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">{t('verifyEmail.verifying')}</p>
          </div>
        ) : success ? (
          <div className="bg-green-50 p-6 rounded-[16px] mb-6 flex flex-col items-center">
            <FaCheckCircle className="text-green-500 text-5xl mb-4" />
            <p className="text-green-700 font-medium text-lg text-center">{t('verifyEmail.success.verified')}</p>
            <p className="text-green-600 text-sm mt-1 text-center">
              {autoLoginMessage || t('verifyEmail.success.successMessage')}
            </p>
            {!autoLoginMessage.includes(t('verifyEmail.success.autoLoginMessage')) && (
              <button 
                onClick={() => navigate('/login')}
                className="w-full h-[60px] rounded-[16px] font-medium text-black mt-6 bg-[#F9D348]"
              >
                {t('verifyEmail.buttons.goToLogin')}
              </button>
            )}
          </div>
        ) : (
          <div className="bg-red-50 p-6 rounded-[16px] mb-6 flex flex-col items-center">
            <FaExclamationTriangle className="text-red-500 text-5xl mb-4" />
            <p className="text-red-700 font-medium text-lg text-center">{t('verifyEmail.error.failed')}</p>
            <p className="text-red-600 text-sm mt-1 text-center">
              {error}
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="w-full h-[60px] rounded-[16px] font-medium text-black mt-6 bg-[#F9D348]"
            >
              {t('verifyEmail.buttons.returnToLogin')}
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {t('verifyEmail.support.needHelp')}{' '}
            <Link to="/contact" className="text-blue-600 font-medium">
              {t('verifyEmail.support.contactSupport')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
} 